import { message } from 'antd';
import { isAxiosError } from 'axios';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/Users/Columns';

import type { User } from '~/types/models';
import { type ErrorResponse, isErrorResponse } from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { getCurrentUser } from '~/redux/auth/selectors';
import { actions as auth } from '~/redux/auth/slice';
import { watchHandleError } from '~/redux/app/saga';

import { actions } from './slice';
import { getItem, getParams } from './selectors';

const call: any = Effects.call;

export function* watchCreate({ payload: item }: PayloadAction<Partial<User>>): Generator<Effect, void, User> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield put(app.title('Création...'));

    const newItem = yield call(Api.create, '/users', item);
    message.success('Utilisateur créé avec succès.');

    yield call(Api.clearCache, /.*users.*/);

    yield put(replace(`/users/${newItem.humanId}/edit`));
  } catch (e) {
    yield put(app.title('Nouvel utilisateur'));
    yield put(actions.setLoading(false));

    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  }
}

export function* watchDestroy({ payload: { id } }: PayloadAction<User>): Generator<Effect, void, void> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield call(Api.destroy, '/users', id);
    message.success('Utilisateur supprimé avec succès.');

    yield call(watchIndex, {});
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchIndex({ payload: params }: PayloadAction<IRestIndexParams<User>>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    const currentParams = yield select(getParams);
    const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/users', {
      ...currentParams,
      ...params,
    });
    yield put(actions.setPage(currentPage));
    yield put(actions.setData(items));
    yield put(actions.setPageSize(pageSize));
    yield put(actions.setTotal(total));
    yield put(actions.setTotalPages(totalPages));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchRetrieve({
  payload: { id, params },
}: PayloadAction<{ id: User['id'] | User['humanId']; params?: IRestIndexParams<User> }>): Generator<
  Effect,
  void,
  User
> {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setError(null));
    let item = yield select(getItem);
    if (item?.humanId !== id) {
      yield put(app.title('Chargement...'));
      yield put(actions.setItem(null));
      yield put(actions.setInitialItem(null));

      item = yield call(Api.retrieve, `/users/${id}`, {
        ...(params ?? {}),
        with: getWithParamString(Columns, 'single'),
      });
      yield put(actions.setItem(item));
      yield put(actions.setInitialItem(JSON.stringify(item)));
    }
    yield put(app.title(item.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSetParams({
  payload: params,
}: PayloadAction<IRestIndexParams<User>>): Generator<Effect, void, any> {
  const { sortBy, page, pageSize, ...filters } = params;

  yield put(actions.setPage(page));
  yield put(actions.setPageSize(pageSize));
  yield put(actions.setSortBy(sortBy));
  yield put(actions.setFilters(filters));
}

export function* watchUpdate({ payload: item }: PayloadAction<Partial<User>>): Generator<Effect, void, any> {
  if (!item.id) {
    return;
  }

  const initialTitle = yield select((state) => state.app.title);
  yield put(actions.setLoading(true));
  yield put(app.title('Sauvegarde...'));
  yield put(actions.setError(null));

  try {
    yield call(Api.update, `/users/${item.id}`, item);
    const updatedItem = yield call(Api.retrieve, `/users/${item.id}`, {
      with: getWithParamString(Columns, 'single'),
    });
    yield put(actions.setItem(updatedItem));
    yield put(actions.setInitialItem(JSON.stringify(updatedItem)));
    message.success('Utilisateur mis-à-jour avec succès.');

    const currentUser = yield select(getCurrentUser);
    if (currentUser?.id === updatedItem.id) {
      const user = yield call(Api.fetchUser);
      yield put(auth.setUser(user));
    }
    yield put(app.title(updatedItem.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(app.title(item.name ?? initialTitle));
    yield put(actions.setLoading(false));
  }
}

export function* watchUsersSagas(): Generator<ForkEffect, void> {
  yield debounce(500, actions.index, watchIndex);
  yield debounce(100, actions.retrieve, watchRetrieve);
  yield takeEvery(actions.create, watchCreate);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.setParams, watchSetParams);
  yield takeEvery(actions.update, watchUpdate);
}

const usersSagas = watchUsersSagas;

export default usersSagas;
